import type { FCWithChildren } from '@pbk-web/types';
import { animated, useTransition } from '@react-spring/web';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

type Banner = {
  id: string;
  icon: ReactNode;
  message: ReactNode | string;
};

type BannerContextType = {
  showBanner: (banner: Omit<Banner, 'id'>) => string;
  removeBanner: (id: string) => void;
  hasBanner: (id: string) => boolean;
};

type InternalBannerContextType = {
  bannerHtml: ReactNode;
};

const BannerContext = createContext<BannerContextType>(null!);
const InternalBannerContext = createContext<InternalBannerContextType>(null!);
export const useBanner = () => useContext(BannerContext);
export const useInternalBanner = () => useContext(InternalBannerContext);

export const BannerProvider: FCWithChildren = (props) => {
  const { children } = props;

  const [banners, setBanners] = useState<Banner[]>([]);

  const showBanner = useCallback((banner: Omit<Banner, 'id'>) => {
    const id = uuid();
    setBanners((prev) => [...prev, { ...banner, id }]);
    return id;
  }, []);

  const removeBanner = useCallback((id: string) => {
    setBanners((prev) => prev.filter((banner) => banner.id !== id));
  }, []);

  const hasBanner = useCallback(
    (id: string) => {
      return banners.some((banner) => banner.id === id);
    },
    [banners],
  );

  const refMap = useMemo(() => new WeakMap<(typeof banners)[number], HTMLDivElement>(), []);
  const transitions = useTransition(banners, {
    from: { opacity: 0, height: 0 },
    keys: (banner) => banner.id,
    enter: (item) => async (next) => {
      await next({ opacity: 1, height: refMap.get(item)?.offsetHeight ?? 0 });
    },
    leave: { opacity: 0, height: 0 },
  });

  const bannerHtml = useMemo(() => {
    return (
      <div className="sticky top-0 z-50 flex flex-col-reverse duration-100 empty:hidden">
        {transitions((style, banner) => (
          <animated.div key={banner.id} style={style} className="bg-greyscale-5 border-greyscale-15 relative box-border overflow-hidden">
            <div
              className="flex items-center gap-2 px-6 py-3"
              ref={(ref) => {
                ref && refMap.set(banner, ref);
              }}
            >
              {banner.icon}
              {banner.message}
            </div>
          </animated.div>
        ))}
      </div>
    );
  }, [refMap, transitions]);

  const values = useMemo(() => {
    return {
      showBanner,
      removeBanner,
      hasBanner,
    };
  }, [hasBanner, removeBanner, showBanner]);

  const internalValues = useMemo(() => {
    return {
      bannerHtml,
    };
  }, [bannerHtml]);

  return (
    <BannerContext.Provider value={values}>
      <InternalBannerContext.Provider value={internalValues}>{children}</InternalBannerContext.Provider>
    </BannerContext.Provider>
  );
};

export const Banners = () => {
  const { bannerHtml } = useInternalBanner();
  return bannerHtml;
};
